import { useEffect, useMemo } from 'react';

import {
  FilterAppliedProps,
  FilterConfig,
  FilterMenuProps,
  Filters,
  useCurrentFilter,
} from '@components/filters';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconStages } from '@components/icons/stages';

import { useStore } from 'models/Provider';
import { IJobStage } from 'models/types';
import { ITODO } from 'types/todo';

import { FilterActionButtons } from './filter-action-buttons';

type ExtraProps = {
  stages: Array<{
    value: string;
    label: string;
  }>;
};

function FilterApplicationStageApplied({ data, stages }: FilterAppliedProps<ExtraProps>) {
  const { t } = useStore();
  let value = '';

  if (Array.isArray(data.value)) {
    value = stages
      .filter((stage) => (data.value as Array<string>).includes(stage.value))
      .map((stage) => stage.label)
      .join(', ');
  } else {
    value = stages.find((stage) => stage.value === data.value)?.label || '';
  }

  return (
    <Filters.AppliedFilter
      icon={<IconStages />}
      category={t('applicants.filters.stage.label')}
      value={value}
      comparer={{
        value: data.comparer!,
        label: t('comparers.' + data.comparer).replace('...', ''),
      }}
    />
  );
}

function FilterApplicationStageMenu({ stages }: FilterMenuProps<ExtraProps>) {
  const { t } = useStore();
  const { state, actions, isMultiComparer, hasValue } = useCurrentFilter();

  const filterValueItems = stages;

  useEffect(() => {
    if (!state.comparer) {
      actions.selectComparer('is');
    }
  }, [actions, state.comparer]);

  return (
    <Filters.List>
      <Filters.ListItem
        heading
        label={t('applicants.filters.stage.label')}
        icon={<IconArrowLeft />}
        onClick={actions.unselectCategory}
      />

      <Filters.ListSeparator />

      <Filters.FilterValueMenu
        items={filterValueItems}
        isMulti={isMultiComparer}
        onSetValue={actions.selectValue}
        state={state}
      />
      <FilterActionButtons disableApply={!hasValue} />
    </Filters.List>
  );
}

export function useFilterApplicationStage(job: ITODO): FilterConfig<ExtraProps> | null {
  const { t } = useStore();
  const stages = job.jobStages.map((stage: IJobStage) => ({
    value: stage.id,
    label: stage.name,
  }));

  return useMemo(() => {
    if (stages.length === 0) return null;

    return {
      category: 'stage',
      label: t('applicants.filters.stage.label'),
      icon: <IconStages />,
      AppliedFilter: FilterApplicationStageApplied,
      Menu: FilterApplicationStageMenu,
      extraProps: {
        stages,
      },
      toQueryData: (data) => {
        switch (data.comparer) {
          case 'is':
          case 'any_of':
            return { job_stage_id: data.value };
          case 'not_is':
          case 'none_of':
            return { job_stage_id__exclude: data.value };
          default:
            return {};
        }
      },
    };
  }, [stages, t]);
}
